import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Grid, Typography } from "@mui/material";
import { FormEvent, useState } from "react";
import { Employee, TagReference } from "../../models";
import { TagSelection } from "./TagSelection";
import { NavBackButton } from "./NavBackButton";
import { SideDrawerTitle } from "../shared/SideDrawer";
import { useBulkApplyUserTags } from "../../hooks/users/useBulkApplyUserTags";
import { useApplyUserTags } from "../../hooks/users/useApplyUserTags";

type ApplyTagsProps = {
  handleComplete: () => void;
  handleClose: () => void;
  selectedEmployees: Employee[];
};

export const ApplyTags = ({
  handleComplete,
  handleClose,
  selectedEmployees
}: ApplyTagsProps) => {
  const [tags, setTags] = useState<TagReference[]>([]);
  const [error, setError] = useState("");

  const applyBulkUserTags = useBulkApplyUserTags({
    onSuccess: () => handleClose(),
    onError: (error: Error) => setError(error.message),
  });

  const applyUserTags = useApplyUserTags({
    onSuccess: () => {
      handleClose();
      handleComplete();
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (selectedEmployees.length === 0) {
      return;
    } else if (selectedEmployees.length <= 10) {
      applyUserTags.mutate({
        employees: selectedEmployees,
        tags
      });
    } else {
      applyBulkUserTags.mutate({
        employeeIds: selectedEmployees.map((x) => x.id),
        tags
      }, {
        onSuccess: () => {
          handleClose();
        }
      });
    }
  }

  return (
    <>
      <NavBackButton onBackPress={handleClose} />
      <Box component={"form"} onSubmit={handleSubmit}>
        <Box display="flex" justifyContent="space-between">
          <SideDrawerTitle sx={{ paddingLeft: 2 }}>
            Apply tags to {selectedEmployees.length} user(s)
          </SideDrawerTitle>
        </Box>
        <Box padding={2}>
          <Grid container spacing={3}>
            {error && (
              <Grid item xs={12}>
                <Typography color="error">{error}</Typography>
              </Grid>
            )}
            <Grid item>
              <Typography>General instructions on assigning tags</Typography>
              <Typography color="gray">
                The tags set here on the selected user(s) will overwrite any
                existing tags applied to these user(s)
              </Typography>
            </Grid>
            <Grid item container gap={2}>
              <Grid item xs={12}>
                <Typography variant="h6">Tags</Typography>
              </Grid>
              <TagSelection tags={tags} setTags={setTags} />
            </Grid>
          </Grid>
        </Box>
        <Box padding={2}>
          <LoadingButton
            type="submit"
            variant="contained"
            fullWidth
            color="primary"
            loading={
              applyBulkUserTags.isLoading || applyUserTags.isLoading
            }
          >
            Save
          </LoadingButton>
        </Box>
      </Box>
    </>
  );
};
